import React, { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import { useSnackbar } from '../../contexts/SnackbarContext';
import GroupModel from '../../models/Group';

interface GroupSelectorProps {
    groups: GroupModel[];
    selectedGroups: (number | string | { id: number; name?: string; group_code?: string; })[];
    onChange: (selectedGroups: (number | string | { id: number; name?: string; group_code?: string; })[]) => void;
    onAddGroup?: (groupData: { name: string; group_code?: string }) => Promise<GroupModel>;
    placeholder?: string;
    disabled?: boolean;
}

const GroupSelector: React.FC<GroupSelectorProps> = ({
    groups,
    selectedGroups,
    onChange,
    onAddGroup,
    placeholder = 'Select Groups',
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAddingGroup, setIsAddingGroup] = useState(false);
    const [newGroupData, setNewGroupData] = useState({ name: '' });
    const { showSuccess, showError } = useSnackbar();

    // Helper function to generate group code from name
    const generateGroupCode = useCallback((name: string): string => {
        if (!name) return `group-${uuidv4().substring(0, 4)}`; // Fallback if name is empty
        const baseCode = name.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
            .replace(/^-+|-+$/g, '');   // Trim leading/trailing hyphens
        // Limit length and add random suffix for uniqueness
        const shortSuffix = uuidv4().substring(0, 4);
        return `${baseCode.substring(0, 15)}-${shortSuffix}`; // Example: limit base name part length
    }, []);

    const handleGroupToggle = useCallback((group: GroupModel) => {
        if (disabled || !group.id) return;

        const groupId = group.id;
        const isSelected = selectedGroups.some(g => {
            if (typeof g === 'object' && g !== null) {
                return (g as any).id === groupId;
            }
            return g === groupId;
        });

        let newSelectedGroups: (number | string | { id: number; name?: string; group_code?: string; })[];

        if (isSelected) {
            // Remove the group
            newSelectedGroups = selectedGroups.filter(g => {
                if (typeof g === 'object' && g !== null) {
                    return (g as any).id !== groupId;
                }
                return g !== groupId;
            });
        } else {
            // Add the group
            newSelectedGroups = [...selectedGroups, {
                id: group.id,
                name: group.name,
                group_code: group.group_code
            }];
        }

        onChange(newSelectedGroups);
    }, [selectedGroups, onChange, disabled]);

    const handleClearSelection = useCallback(() => {
        if (disabled) return;
        onChange([]);
    }, [onChange, disabled]);

    const handleAddGroupClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        setIsAddModalOpen(true);
        setIsOpen(false);
    }, [disabled]);

    const handleAddGroupInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewGroupData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSaveNewGroup = useCallback(async () => {
        if (!newGroupData.name.trim()) {
            showError('Group name is required.');
            return;
        }

        if (!onAddGroup) {
            showError('Add group functionality is not available.');
            return;
        }

        setIsAddingGroup(true);

        try {
            // Generate the group code automatically
            const generatedCode = generateGroupCode(newGroupData.name.trim());

            const newGroup = await onAddGroup({
                name: newGroupData.name.trim(),
                group_code: generatedCode
            });

            if (newGroup) {
                showSuccess(`Group "${newGroup.name}" added successfully!`);
                setIsAddModalOpen(false);
                setNewGroupData({ name: '' });

                // Automatically select the newly added group
                const groupForSelection = {
                    id: newGroup.id!,
                    name: newGroup.name,
                    group_code: newGroup.group_code
                };
                const newSelectedGroups = [...selectedGroups, groupForSelection];
                onChange(newSelectedGroups);
            }
        } catch (error) {
            console.error('Error adding group:', error);
            showError('Failed to add group. Please try again.');
        } finally {
            setIsAddingGroup(false);
        }
    }, [newGroupData.name, onAddGroup, generateGroupCode, showSuccess, showError]);

    const handleCloseAddModal = useCallback(() => {
        setIsAddModalOpen(false);
        setNewGroupData({ name: '' });
    }, []);

    const getButtonText = () => {
        if (selectedGroups.length === 0) return placeholder;
        if (selectedGroups.length === 1) {
            const group = groups.find(g => g.id === (selectedGroups[0] as any)?.id || g.id === selectedGroups[0]);
            return group?.name || 'Selected Group';
        }
        return `${selectedGroups.length} groups selected`;
    };

    const displayGroups = groups.filter(group => group.name && group.name.trim() !== '' && group.id);

    return (
        <>
            <div className="relative">
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    <span className="flex-grow truncate text-left">{getButtonText()}</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {selectedGroups.length > 0 && !disabled && (
                    <button
                        type="button"
                        onClick={handleClearSelection}
                        className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 rounded"
                        title="Clear selection"
                        aria-label="Clear selection"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 flex flex-col overflow-hidden">
                        {/* Scrollable groups list */}
                        <div className="overflow-y-auto max-h-60">
                            <ul className="py-1">
                                {displayGroups.length > 0 ? (
                                    displayGroups.map(group => {
                                        const isSelected = selectedGroups.some(g => {
                                            if (typeof g === 'object' && g !== null) {
                                                return (g as any).id === group.id;
                                            }
                                            return g === group.id;
                                        });

                                        return (
                                            <li
                                                key={group.id}
                                                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center"
                                                onClick={() => handleGroupToggle(group)}
                                                role="option"
                                                aria-selected={isSelected}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    readOnly
                                                    className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer pointer-events-none"
                                                    tabIndex={-1}
                                                />
                                                <span>{group.name}</span>
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li className="px-3 py-2 text-sm text-gray-500 italic">No groups available</li>
                                )}
                            </ul>
                        </div>

                        {/* Fixed Add Group Option at the bottom */}
                        {onAddGroup && (
                            <div className="border-t border-gray-200 flex-shrink-0 bg-white">
                                <div
                                    className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer flex items-center"
                                    onClick={handleAddGroupClick}
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span className="font-medium">Add New Group</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add Group Modal */}
            <Modal isOpen={isAddModalOpen} onClose={handleCloseAddModal} title="Add New Group">
                <div className="space-y-4">
                    <div>
                        <label htmlFor="add-group-name" className="block text-sm font-medium text-gray-700 mb-1">
                            Group Name *
                        </label>
                        <Input
                            id="add-group-name"
                            name="name"
                            value={newGroupData.name}
                            onChange={handleAddGroupInputChange}
                            placeholder="e.g., Cinematography Year 1"
                            required
                            autoFocus
                            disabled={isAddingGroup}
                            label=""
                            minLength={0}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            A unique group code will be generated automatically.
                        </p>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleCloseAddModal}
                            disabled={isAddingGroup}
                            title="Cancel adding group"
                            aria-label="Cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleSaveNewGroup}
                            disabled={isAddingGroup || !newGroupData.name.trim()}
                            title="Add new group"
                            aria-label="Add Group"
                        >
                            {isAddingGroup ? 'Adding...' : 'Add Group'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default GroupSelector;
